import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {switchMap,map, catchError, of, filter, exhaustMap, tap } from 'rxjs';
import { VoteScorecardApiService } from '../services/vote-scorecard-api.service';
import { displayNotificationMessage, getCongressionalDistrict, getCongressionalDistrictFailure, getCongressionalDistrictSuccess, getCongressmenDatatype, getCongressmenDatatypeFailure, getCongressmenDatatypeSuccess,  getLegislation, getLegislationFailure, getLegislationSuccess, getParties, getPartiesFailure, getPartiesSuccess, getScorecard, getScorecardFailure, getScorecardSuccess, getStates, getStatesFailure, getStatesSuccess, getVoteTypes, getVoteTypesFailure, getVoteTypesSuccess } from './core.actions';
import { SnackBarService } from '../services/snack-bar.service';
import { SnackBarTheme } from '../models/snackbar-data';
import { Router } from '@angular/router';
import { CongressionalDistrictDto } from '../models/congressional-district';


@Injectable()
export class CoreEffects {
  constructor(
    private actions$: Actions,
    private voteScorecardApiService: VoteScorecardApiService,
    private snackbar: SnackBarService,
    private router: Router
  ) {}

  getStates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getStates),
      switchMap(() =>
        this.voteScorecardApiService.getStates().pipe(
          map((states) => (getStatesSuccess({ states }))),
          catchError((error: HttpErrorResponse) =>
            of(getStatesFailure({ error }))  
          )
        )
      )
    )
  );

  getLegislation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLegislation),
      switchMap(() =>
        this.voteScorecardApiService.getLegislation().pipe(
          map(Legislation => (getLegislationSuccess({ Legislation }))),
          catchError(error => of(getLegislationFailure({ error })))
        )
      )
    )
  );

  getParties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getParties),
      switchMap(() =>
        this.voteScorecardApiService.getParties().pipe(
          map((parties) => (getPartiesSuccess({ parties }))),
          catchError((error: HttpErrorResponse) =>
            of(getPartiesFailure({ error }))  
          )
        )
      )
    )
  );

  getVoteTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getVoteTypes),
      switchMap(() =>
        this.voteScorecardApiService.getVoteTypes().pipe(
          map(voteTypes => (getVoteTypesSuccess({ voteTypes }))),
          catchError(error => of(getVoteTypesFailure({ error })))
        )
      )
    )
  );

  getCongressmenDatatype$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCongressmenDatatype),
      exhaustMap(() =>
        this.voteScorecardApiService.getCongressmenDatatype().pipe(
          map(congressmenDisplayNames => (getCongressmenDatatypeSuccess({ congressmenDisplayNames }))),
          catchError(error => of(getCongressmenDatatypeFailure({ error })))
        )
      )
    )
  );

  getCongressionalDistrict$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCongressionalDistrict),
      filter(({ address, localUnion }) => !!address || !!localUnion),
      switchMap(({ address, localUnion }) =>
        this.voteScorecardApiService.getCongressionalDistricts(address, localUnion).pipe(
          map((districts: CongressionalDistrictDto[]) =>
            districts.map(d => ({
              stateId: d.stateId,
              districtNumber: d.district,
              localUnion: d.localUnionNumber ?? undefined
            }))
          ),
          map((districtsWithNumber) =>
            getCongressionalDistrictSuccess({ congressionalDistricts: districtsWithNumber })
          ),
          catchError((error: HttpErrorResponse) => {
            return of(
              getCongressionalDistrictFailure({ error }),
              displayNotificationMessage({
                data: {
                  snackBarSummary: 'Error',
                  snackBarDetail: 'No congressional district found for that input. Please enter a valid address, local union, or district (e.g., \'TX-1\').',
                  snackBarTheme: SnackBarTheme.ERROR,
                },
              })
            );
          })
          
        )
      )
    ))

  getScorecard$ = createEffect(() =>
  this.actions$.pipe(
    ofType(getScorecard),
    switchMap(({ congressmanId, stateId, districtNumber, localUnion }) =>
      this.voteScorecardApiService
        .getScorecard(congressmanId, stateId, districtNumber, localUnion)
        .pipe(
          map(scorecards => getScorecardSuccess({ scorecards })),
          catchError((error: HttpErrorResponse) => {
            return of(
              getScorecardFailure({ error }),
              displayNotificationMessage({
                data: {
                  snackBarSummary: 'Error',
                  snackBarDetail: `${error?.error?.error ?? 'No congressional district found for that input. Please enter a valid address, local union, or district (e.g., \'TX-1\').'}`,
                  snackBarTheme: SnackBarTheme.ERROR,
                },
              })
            );
          })
        )
    )
  )
);

navigateOnScorecardFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getScorecardFailure),
      tap(() => this.router.navigate(['/']))
    ),{ dispatch: false }
);



displayNotificationMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(displayNotificationMessage),
      map((action) => this.snackbar.openSnackBar(action.data))
    ),{ dispatch: false }
  );
}